import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { listGroups, createGroup, archiveGroup, type Group } from '../lib/api';


export function GroupsPage() {
  const { user } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    listGroups(user.orgId)
      .then((g) => { setGroups(g.filter((x) => !x.archivedAt)); setLoading(false); })
      .catch(() => { setError('Failed to load groups.'); setLoading(false); });
  }, [user]);

  if (!user) return null;

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const g = await createGroup(user!.orgId, { name: name.trim(), description: description.trim() || undefined });
      setGroups((prev) => [g, ...prev]);
      setShowForm(false);
      setName('');
      setDescription('');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleArchive(groupId: string, groupName: string) {
    if (!confirm(`Archive group "${groupName}"? Members stay in the org.`)) return;
    await archiveGroup(user!.orgId, groupId);
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Groups</h1>
          <p className="page-subtitle">Departments, regions, cohorts, or programs within {user.orgName}.</p>
        </div>
        <button className="btn btn--primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : '+ New group'}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 'var(--space-6)', maxWidth: '480px' }}>
          <h2 style={{ fontSize: '1rem', marginBottom: 'var(--space-4)' }}>Create group</h2>
          <form onSubmit={handleCreate}>
            <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
              <label className="form-label" htmlFor="group-name">Name</label>
              <input
                id="group-name"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Engineering, People Ops…"
                required
                autoFocus
              />
            </div>
            <div className="form-group" style={{ marginBottom: 'var(--space-5)' }}>
              <label className="form-label" htmlFor="group-desc">Description (optional)</label>
              <input
                id="group-desc"
                className="form-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this group is for…"
              />
            </div>
            <button type="submit" className="btn btn--primary" disabled={submitting || !name.trim()}>
              {submitting ? 'Creating…' : 'Create group'}
            </button>
          </form>
        </div>
      )}

      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {!loading && !error && groups.length === 0 && (
        <div className="empty-state">
          <p className="empty-state__title">No groups yet</p>
          <p className="empty-state__body">
            Groups organize your teams by department, region, or cohort.
          </p>
        </div>
      )}

      {!loading && groups.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 'var(--space-4)',
          }}
        >
          {groups.map((g) => (
            <div key={g.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <h3 style={{ fontSize: '1rem' }}>{g.name}</h3>
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => handleArchive(g.id, g.name)}
                  style={{ color: 'var(--color-text-subtle)', marginTop: '-2px' }}
                  aria-label={`Archive ${g.name}`}
                >
                  Archive
                </button>
              </div>
              {g.description && (
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginTop: 'var(--space-2)' }}>
                  {g.description}
                </p>
              )}
              <div
                style={{
                  marginTop: 'var(--space-4)',
                  display: 'flex',
                  gap: 'var(--space-4)',
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-subtle)',
                }}
              >
                <span>{g.teamCount} team{g.teamCount !== 1 ? 's' : ''}</span>
                <span>{g.memberCount} member{g.memberCount !== 1 ? 's' : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
